import { action, computed } from 'mobx';
import type { ContextFilterJson } from '@kentik/schema/gen/ts/kentik/alerting/context/v202602/filter_pb';

import { contextFilterToFormValues } from 'app/util/contextFilters';

import { Model } from 'core/model/Model';
import api from 'core/util/api';
import { timezone, formatDateTime, humanizeDuration } from 'core/util/dateUtils';
import {
  ALERT_SEVERITY_LABELS,
  SUPPRESSION_TYPES,
  SUPPRESSION_TYPE_LABELS,
  SUPPRESSION_TIMESTAMP_SETTINGS
} from '@kentik/ui-shared/alerting/constants';
import safelyParseJSON from '@kentik/ui-shared/util/safelyParseJson';
import {
  getFilterOperator,
  CONDITION_OPERATOR_MAP,
  BOOLEAN_CONDITION_OPERATORS,
  isEditableKeyValueFilter
} from 'app/forms/config/suppressions/suppression';
import $syn from 'app/stores/synthetics/$syn';
import $alerting from 'app/stores/alerting/$alerting';
import { getSuppressionReferenceIds } from '@kentik/ui-shared/alerting/suppressionReferenceIds';
import {
  getUnavailableReferences,
  unavailableReferenceLabel,
  unknownDimensionReferences,
  type SuppressionReference
} from './suppressionReferences';
import { getUnknownDimensions } from './suppressionDimensions';
import { deserializeSuppressionApplications, getSuppressionApplicationLabel } from './suppressionApplications';
import type { SuppressionLifecycleStatus, SuppressionStatus } from './suppressionLifecycle';
import {
  getSuppressionLifecycleStatus,
  isManuallyStopped,
  SUPPRESSION_LIFECYCLE_STATUS,
  SUPPRESSION_STATUS
} from './suppressionLifecycle';

export interface SuppressionStringMatch {
  equals?: string;
  in?: { values: string[] };
}

export interface SuppressionFilterValue extends SuppressionStringMatch {
  contains?: string;
  starts_with?: string;
  ends_with?: string;
  matches_regex?: string;
  any?: boolean;
  empty?: boolean;
  not?: boolean;
}

export interface SuppressionKeyFilterItem {
  key: SuppressionStringMatch;
  value: SuppressionFilterValue;
}

export interface SuppressionKeyFilter {
  filter?: Record<string, SuppressionFilterValue>;
  filters: SuppressionKeyFilterItem[];
  strict?: boolean;
}

export interface SuppressionApplicationMetadata {
  type?: string;
  measurement?: string;
  policyId?: string;
}

export interface SuppressionUiProperties {
  startAsSoonAsPossible?: boolean;
  fetchedAt?: number;
  applicationMetadata?: SuppressionApplicationMetadata;
}

export interface SuppressionFormFilter {
  filterField: string;
  filterOperator?: string;
  filterValue?: unknown;
}

export type SuppressionAttributes = {
  id: string | number;
  context_filter?: ContextFilterJson | null;
  display_name?: string;
  user_id?: string;
  rule_id?: string;
  rule_ids?: string[];
  rule_application?: string;
  severities?: string[];
  applications?: string[];
  comment?: string;
  strict?: boolean;
  key_filter?: SuppressionKeyFilter;
  start_time?: string | null;
  start_time_code?: string;
  end_time?: string | null;
  end_time_code?: string;
  application_metadata?: string;
  cdate?: string;
  edate?: string;
};

type SuppressionUiState = {
  uiProperties?: SuppressionUiProperties;
  tableKey?: string;
};

export type SuppressionModelAttributes = SuppressionAttributes & SuppressionUiState;

const LEGACY_UNTYPED_SUPPRESSION_TYPES: string[] = [SUPPRESSION_TYPES.POLICY, 'partial', 'full'];

export default class SuppressionModel extends Model<SuppressionModelAttributes> {
  protected get omitDuringSerialize(): Readonly<(keyof SuppressionModelAttributes)[]> {
    return ['uiProperties', 'tableKey', 'user_id'];
  }

  get urlRoot() {
    return '/api/ui/alertingManager/suppressions';
  }

  @action
  fetch(options: Record<string, unknown> = {}): Promise<void | SuppressionModelAttributes> {
    const label = 'fetching';
    this.requestStatus = label;
    this.requestProgress = 0;

    return api.get(this.url, { ...options, rawResponse: true }).then(
      (success: { body?: Partial<SuppressionAttributes> }) => {
        this.set(this.deserialize(success.body ?? {}));
        this.error = null;
        this.setRequestStatus(null);
        return this.get();
      },
      (error: unknown) => {
        this.error = { label, body: error };
        this.requestStatus = null;
        throw error;
      }
    );
  }

  keepsExistingStartTime(startTime?: string | null) {
    const existingStartTime = this.get('start_time');

    if (this.isNew || !existingStartTime) {
      return false;
    }

    return !startTime || timezone.momentFn(startTime).isSame(existingStartTime);
  }

  serialize(data: Partial<SuppressionModelAttributes> = {}) {
    const { uiProperties, start_time, end_time } = data;
    const ruleIds = data.rule_ids ?? (data.rule_id ? [data.rule_id] : undefined);
    const hasEndTime = Object.prototype.hasOwnProperty.call(data, 'end_time');
    const suppression = super.serialize({
      ...data,
      rule_id: undefined,
      rule_ids: ruleIds ? [...new Set(ruleIds)] : undefined,
      start_time: undefined,
      end_time: hasEndTime ? end_time : undefined
    }) as Partial<SuppressionAttributes>;
    delete suppression.rule_id;
    const applicationMetadata = safelyParseJSON(suppression.application_metadata || '');
    if (applicationMetadata && typeof applicationMetadata === 'object' && !Array.isArray(applicationMetadata)) {
      const currentApplicationMetadata = Object.fromEntries(
        Object.entries(applicationMetadata).filter(([key]) => key !== 'application')
      );
      suppression.application_metadata = JSON.stringify(currentApplicationMetadata, null, 2);
    }

    if (Object.prototype.hasOwnProperty.call(data, 'start_time') || uiProperties?.startAsSoonAsPossible) {
      if (uiProperties?.startAsSoonAsPossible) {
        suppression.start_time_code = SUPPRESSION_TIMESTAMP_SETTINGS.AS_SOON_AS_POSSIBLE;
      } else if (this.keepsExistingStartTime(start_time)) {
        suppression.start_time_code = SUPPRESSION_TIMESTAMP_SETTINGS.KEEP_UNCHANGED;
      } else {
        suppression.start_time = start_time;
        suppression.start_time_code = SUPPRESSION_TIMESTAMP_SETTINGS.UNSPECIFIED;
      }
    }

    if (hasEndTime && !Object.prototype.hasOwnProperty.call(data, 'end_time_code')) {
      suppression.end_time_code = SUPPRESSION_TIMESTAMP_SETTINGS.UNSPECIFIED;
    }

    return suppression;
  }

  async updateOngoing({
    endTime,
    displayName
  }: {
    endTime: string | null;
    displayName: string;
  }): Promise<void | SuppressionModelAttributes> {
    return this.save(
      {
        display_name: displayName,
        end_time: endTime,
        end_time_code: SUPPRESSION_TIMESTAMP_SETTINGS.UNSPECIFIED
      },
      { sendProvidedValuesOnly: true, preserialize: false, toast: false }
    );
  }

  async stop(): Promise<void | SuppressionModelAttributes> {
    return this.save(
      {
        end_time_code: SUPPRESSION_TIMESTAMP_SETTINGS.AS_SOON_AS_POSSIBLE
      },
      { sendProvidedValuesOnly: true, preserialize: false, toast: false, clearSelection: false }
    );
  }

  deserialize(data: Partial<SuppressionAttributes> = {}) {
    const suppression = super.deserialize(data);
    const parsedApplicationMetadata =
      (safelyParseJSON(suppression.application_metadata || '') as SuppressionApplicationMetadata | undefined) || {};
    const { application: _legacyApplication, ...applicationMetadata } =
      parsedApplicationMetadata as SuppressionApplicationMetadata & { application?: string };
    const { rule_id: legacyRuleId, ...currentSuppression } = suppression;
    const ruleIds = suppression.rule_ids ?? (legacyRuleId ? [legacyRuleId] : undefined);
    const serializedApplicationMetadata =
      suppression.application_metadata === undefined ? undefined : JSON.stringify(applicationMetadata);

    return {
      ...currentSuppression,
      application_metadata: serializedApplicationMetadata,
      rule_ids: ruleIds,
      uiProperties: {
        startAsSoonAsPossible: !suppression.start_time,
        fetchedAt: Date.now(),
        applicationMetadata
      }
    };
  }

  protected get sortValues() {
    return {
      expirationDetails: (model: SuppressionModel) => model.get('end_time') ?? ''
    };
  }

  @computed
  get expired() {
    return this.lifecycleStatus === SUPPRESSION_LIFECYCLE_STATUS.EXPIRED;
  }

  @computed
  get expirationDetails() {
    if (this.isManuallyStopped) {
      return { expirationDay: null, expired: true, duration: null };
    }

    const expirationTime = this.get('end_time');

    if (!expirationTime) {
      return { expirationDay: null, expired: false, duration: null };
    }

    return {
      expirationDay: formatDateTime(expirationTime),
      duration: humanizeDuration(timezone.momentFn(expirationTime).diff(undefined))
    };
  }

  @computed
  get formattedStartTime() {
    const startTime = this.get('start_time');
    return formatDateTime(startTime);
  }

  @computed
  get startDetails() {
    const startTimeMoment = timezone.momentFn(this.get('start_time'));
    return {
      started: startTimeMoment.isSameOrBefore(),
      futureStart: startTimeMoment.isAfter(),
      diff: humanizeDuration(startTimeMoment.diff(undefined))
    };
  }

  @computed
  get isActive(): boolean {
    return this.lifecycleStatus === SUPPRESSION_LIFECYCLE_STATUS.ONGOING;
  }

  @computed
  get isManuallyStopped(): boolean {
    return isManuallyStopped(this.get('end_time_code'));
  }

  @computed
  get lifecycleStatus(): SuppressionLifecycleStatus {
    return getSuppressionLifecycleStatus({
      start_time: this.get('start_time'),
      end_time: this.get('end_time'),
      end_time_code: this.get('end_time_code')
    });
  }

  @computed
  get invalidReferences(): SuppressionReference[] {
    const unknownDimensions = getUnknownDimensions(
      this.rawFilters.map(({ filterField }) => filterField),
      { type: this.type, measurement: this.measurement }
    );

    return [
      ...getUnavailableReferences(
        { ruleIds: this.rule_ids, testIds: this.rawTestIds, agentIds: this.rawAgentIds },
        { $syn, $alerting }
      ),
      ...unknownDimensionReferences(unknownDimensions)
    ];
  }

  @computed
  get status(): SuppressionStatus {
    return this.lifecycleStatus !== SUPPRESSION_LIFECYCLE_STATUS.EXPIRED && this.invalidReferences.length > 0
      ? SUPPRESSION_STATUS.INVALID
      : this.lifecycleStatus;
  }

  @computed
  get createdDay() {
    return formatDateTime(this.get('cdate'));
  }

  get comment(): string | undefined {
    return this.get('comment');
  }

  get name(): string | undefined {
    return this.get('display_name')?.trim() || undefined;
  }

  get displayName(): string {
    return this.name ?? 'Unnamed suppression';
  }

  @computed
  get hasContextFilter(): boolean {
    return Boolean(this.get('context_filter')?.conjunctions?.length);
  }

  @computed
  get hasPatternRestriction(): boolean {
    return (this.get('key_filter')?.filters || []).length > 0;
  }

  @computed
  get contextFilterEntries(): Array<{ field: string; value: string }> {
    const { conjunctions } = contextFilterToFormValues(this.get('context_filter'));

    return conjunctions.flatMap(({ clauses }) =>
      clauses.flatMap(({ predicates }) =>
        predicates.flatMap(({ field, value }) =>
          (Array.isArray(value) ? value.map(String) : [String(value ?? '')]).map((entry) => ({ field, value: entry }))
        )
      )
    );
  }

  get rule_ids(): string[] {
    return this.get('rule_ids') || [];
  }

  get messages(): { create: string; update: string; destroy: string; duplicate: string } {
    return {
      create: `Suppression created${this.name ? ` '${this.name}'` : ''}. It will take effect in a few minutes.`,
      update: `Suppression updated${this.name ? ` '${this.name}'` : ''}`,
      destroy: `Suppression ${this.comment ? `for '${this.comment}' was` : ''} removed successfully`,
      duplicate: `Suppression ${this.comment ? `for '${this.comment}' was` : ''} duplicated successfully`
    };
  }

  @computed
  get rawAgentIds(): string[] {
    return getSuppressionReferenceIds(this.get('key_filter'), ['agentId', 'targetAgentId']);
  }

  @computed
  get rawTestIds(): string[] {
    return getSuppressionReferenceIds(this.get('key_filter'), ['testId']);
  }

  get measurement() {
    return this.get('uiProperties')?.applicationMetadata?.measurement;
  }

  @computed
  get type() {
    const { type } = this.get('uiProperties')?.applicationMetadata || {};
    const hasFilters = (this.get('key_filter')?.filters || []).length > 0;

    // raw ids, so a suppression whose tests/agents were deleted doesn't degrade into an unscoped condition
    if (this.rawAgentIds.length > 0) {
      return SUPPRESSION_TYPES.AGENT;
    }

    if (this.rawTestIds.length > 0) {
      return SUPPRESSION_TYPES.SYNTH;
    }

    if (!type || LEGACY_UNTYPED_SUPPRESSION_TYPES.includes(type)) {
      return hasFilters ? SUPPRESSION_TYPES.CONDITION : SUPPRESSION_TYPES.NONE;
    }

    return type;
  }

  @computed
  get hasAlertType(): boolean {
    return this.type !== SUPPRESSION_TYPES.NONE;
  }

  get isSuppression(): true {
    return true;
  }

  /** Both models answer both questions so the mixed suppression/silence list can discriminate. */
  get isSilence(): false {
    return false;
  }

  get typeLabel(): string {
    return SUPPRESSION_TYPE_LABELS[this.type];
  }

  @computed
  get policyDisplayNames(): string[] {
    return this.rule_ids.map((ruleId) => {
      const policy = $alerting.policyCollection.getPolicyByRuleId(ruleId);
      return policy?.name || unavailableReferenceLabel('policy');
    });
  }

  get applicationDisplayNames(): string[] {
    return (deserializeSuppressionApplications(this.get('applications')) || []).map(getSuppressionApplicationLabel);
  }

  get severityDisplayNames(): string[] {
    return (this.get('severities') || []).map((severity) => ALERT_SEVERITY_LABELS[severity] || severity);
  }

  @computed
  get dimensions() {
    const filters = this.get('key_filter')?.filters || [];

    return filters.reduce<Record<string, unknown>>((allFilters, { key, value }) => {
      const dimensionNames = key?.equals ? [key.equals] : key?.in?.values || [];
      const dimensionValue = value?.equals ?? value?.in?.values;

      dimensionNames.forEach((dimensionName) => {
        allFilters[dimensionName] = dimensionValue;
      });

      return allFilters;
    }, {});
  }

  matchesDimensionTarget(target: Record<string, unknown> = {}, strict = true): boolean {
    const filters = this.get('key_filter')?.filters || [];

    if (!filters.length) {
      return !strict;
    }

    const covered = new Set<string>();
    const matches = filters.every((filter) => {
      const keys: string[] = filter.key?.equals ? [filter.key.equals] : filter.key?.in?.values || [];
      const expected =
        filter.value?.equals !== undefined && filter.value?.equals !== null
          ? [filter.value.equals]
          : filter.value?.in?.values || [];
      const hit = keys.find((key) => expected.map(String).includes(String(target[key])));

      if (hit) {
        covered.add(hit);
      }

      return Boolean(hit);
    });

    if (!matches) {
      return false;
    }

    if (!strict || filters.some((filter) => filter.key?.in?.values?.length)) {
      return true;
    }

    const targetKeys = Object.keys(target).filter((key) => target[key] !== undefined && target[key] !== '');

    return targetKeys.length === covered.size && targetKeys.every((key) => covered.has(key));
  }

  get rawFilters(): SuppressionFormFilter[] {
    const filters = this.get('key_filter')?.filters || [];

    return filters.reduce<SuppressionFormFilter[]>((acc, filter) => {
      if (!isEditableKeyValueFilter(filter)) {
        return acc;
      }

      const { key, value } = filter;
      const fieldName = key?.equals;

      if (!fieldName) {
        return acc;
      }

      const filterOperator = getFilterOperator(value);
      const operatorKey = CONDITION_OPERATOR_MAP[filterOperator].key;
      const isBooleanOperator = BOOLEAN_CONDITION_OPERATORS.includes(operatorKey);
      let filterValue: unknown = value[operatorKey as keyof SuppressionFilterValue];

      if (isBooleanOperator) {
        filterValue = null;
      } else if (operatorKey === 'in') {
        filterValue = value.in?.values;
      }

      return [
        ...acc,
        {
          filterField: fieldName,
          filterOperator,
          filterValue
        }
      ];
    }, []);
  }
}
